import { DownOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  List,
  Popover,
  Typography,
} from 'antd';
import cn from 'classnames';
import { useCallback, useMemo, useState, type FC } from 'react';

import type { CheckboxFilterOptions } from '@app/components/CheckboxFilter/types';
import { ArchiveLabel } from '@app/shared/components';
import { FilterMenuShell } from '@app/shared/components/FilterMenuShell';
import { SocialIntegrationIcon } from '@app/shared/ui';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import type { CheckboxChangeEvent } from 'antd/lib';

import '@app/components/CheckboxFilter/styles/CheckboxFilter.desktop.scss';

interface Props {
  title: string;
  disabled?: boolean;
  options: CheckboxFilterOptions[];
  onChange(checkedValue: string[]): void;
  checkedValues: string[];
  dataTestId?: string;
  popoverRootClassName?: string;
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
}

const PF = 'CheckboxFilter';
const { Text } = Typography;
const LIST_HEIGHT = 320;
const ROW_HEIGHT = 32;
const ROW_OVERSCAN = 5;

interface CheckboxFilterOptionRowProps {
  option: CheckboxFilterOptions;
  checked: boolean;
  disabled: boolean;
  onChange(id: string, checked: boolean): void;
}

interface VirtualizedOptionsListProps {
  options: CheckboxFilterOptions[];
  checkedValuesSet: Set<string>;
  disabled: boolean;
  onOptionChange(id: string, checked: boolean): void;
}

const CheckboxFilterOptionRow: FC<CheckboxFilterOptionRowProps> = ({
  option: { id, label, bold, iconId, isDeleted },
  checked,
  disabled,
  onChange,
}) => {
  const changeHandler = useCallback(
    ({ target: { checked: nextChecked } }: CheckboxChangeEvent) => {
      onChange(id, nextChecked);
    },
    [id, onChange]
  );

  return (
    <List.Item className={`${PF}__item`} data-testid="list-value">
      <Checkbox
        value={id}
        checked={checked}
        disabled={disabled}
        onChange={changeHandler}
        data-testid="checkbox"
      >
        <Flex align="center" gap={6}>
          <SocialIntegrationIcon type={iconId} size="sm" />

          {isDeleted && <ArchiveLabel />}
          <Text
            strong={bold}
            className="no-wrap-text"
            style={{ maxWidth: 302 }}
          >
            {label}
          </Text>
        </Flex>
      </Checkbox>
    </List.Item>
  );
};

const VirtualizedOptionsList: FC<VirtualizedOptionsListProps> = ({
  options,
  checkedValuesSet,
  disabled,
  onOptionChange,
}) => {
  const [scrollTop, setScrollTop] = useState(0);

  const totalHeight = options.length * ROW_HEIGHT;
  const visibleRowsCount = Math.ceil(LIST_HEIGHT / ROW_HEIGHT);
  const maxStartIndex = Math.max(0, options.length - visibleRowsCount);
  const startIndex = Math.min(
    maxStartIndex,
    Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - ROW_OVERSCAN)
  );
  const endIndex = Math.min(
    options.length,
    startIndex + visibleRowsCount + ROW_OVERSCAN * 2
  );
  const visibleOptions = options.slice(startIndex, endIndex);
  const offsetTop = startIndex * ROW_HEIGHT;

  if (options.length === 0) {
    return <div className={`${PF}__empty`}>Нет результатов</div>;
  }

  return (
    <div
      className={`${PF}__list full-width`}
      style={{ height: Math.min(LIST_HEIGHT, totalHeight) }}
      onScroll={({ currentTarget: { scrollTop } }) => setScrollTop(scrollTop)}
    >
      <div className={`${PF}__spacer`} style={{ height: totalHeight }}>
        <List
          itemLayout="horizontal"
          dataSource={visibleOptions}
          rowKey={({ id }) => id}
          rootClassName={`${PF}__visible-list full-width`}
          style={{ transform: `translateY(${offsetTop}px)` }}
          renderItem={(option) => (
            <CheckboxFilterOptionRow
              option={option}
              checked={checkedValuesSet.has(option.id)}
              disabled={disabled}
              onChange={onOptionChange}
            />
          )}
        />
      </div>
    </div>
  );
};

export const CheckboxFilter: FC<Props> = ({
  title,
  disabled = false,
  options = [],
  onChange,
  checkedValues,
  dataTestId = '',
  popoverRootClassName,
  getPopupContainer,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return options;

    return options.filter(
      ({ label, id }) =>
        label.toLowerCase().includes(query) || id.toLowerCase().includes(query)
    );
  }, [options, searchValue]);

  const checkedValuesSet = useMemo(
    () => new Set<string>(checkedValues),
    [checkedValues]
  );

  const hasSearchScope = useMemo(
    () =>
      searchValue.trim().length > 0 &&
      filteredOptions.length !== options.length,
    [searchValue, filteredOptions, options]
  );

  const filteredIds = useMemo(
    () => filteredOptions.map(({ id }) => id),
    [filteredOptions]
  );

  const allIds = useMemo(() => options.map(({ id }) => id), [options]);

  const scopeIds = useMemo(
    () => (hasSearchScope ? filteredIds : allIds),
    [filteredIds, allIds, hasSearchScope]
  );

  const checkAll = useMemo(
    () =>
      scopeIds?.length > 0 && scopeIds?.every((id) => checkedValuesSet.has(id)),
    [scopeIds, checkedValuesSet]
  );

  const indeterminate = useMemo(() => {
    const someCheckedInScope = scopeIds.some((id) => checkedValuesSet.has(id));
    return someCheckedInScope && !checkAll;
  }, [scopeIds, checkedValuesSet, checkAll]);

  const onCheckAllChange = useCallback(
    ({ target: { checked: nextChecked } }: CheckboxChangeEvent) => {
      const idsInScope = hasSearchScope ? filteredIds : allIds;

      if (hasSearchScope) {
        const scopeSet = new Set<string>(idsInScope);
        const outsideScope = (checkedValues || []).filter(
          (id) => !scopeSet.has(id)
        );
        const next = nextChecked
          ? [...outsideScope, ...idsInScope]
          : outsideScope;

        onChange(Array.from(new Set<string>(next)));
        return;
      }

      onChange(nextChecked ? allIds : []);
    },
    [hasSearchScope, filteredIds, allIds, onChange, checkedValues]
  );

  const optionChangeHandler = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        onChange(Array.from(new Set<string>([...checkedValues, id])));
        return;
      }

      onChange(checkedValues.filter((checkedId) => checkedId !== id));
    },
    [checkedValues, onChange]
  );

  const count = useMemo(() => {
    if (!checkedValues || checkedValues.length === 0) return '';
    if (options.length > 0 && checkedValues.length === options.length)
      return 'Все';
    return String(checkedValues.length);
  }, [checkedValues, options]);

  const isActive = checkedValues.length > 0;

  const orderedOptions = useMemo(() => {
    const selected: CheckboxFilterOptions[] = [];
    const unselected: CheckboxFilterOptions[] = [];

    for (const o of filteredOptions) {
      (checkedValuesSet.has(o.id) ? selected : unselected).push(o);
    }
    return [...selected, ...unselected];
  }, [filteredOptions, checkedValuesSet]);

  return (
    <Popover
      content={
        <FilterMenuShell
          className={`${PF}__popover`}
          title={title}
          meta={count ? `Выбрано: ${count}` : undefined}
        >
          <Input
            className={`${PF}__search full-width`}
            placeholder="Поиск"
            disabled={disabled}
            value={searchValue}
            onChange={({ target: { value } }) => setSearchValue(value)}
            data-testid="filter-search-input"
          />
          {scopeIds.length > 0 && (
            <>
              <Checkbox
                indeterminate={indeterminate}
                onChange={onCheckAllChange}
                checked={checkAll}
                disabled={disabled || scopeIds.length === 0}
                data-testid="checkbox"
              >
                {hasSearchScope
                  ? generateSpanWithDataTestId(
                      'Выбрать найденное',
                      'select-by-search'
                    )
                  : generateSpanWithDataTestId(
                      'Выбрать все',
                      'select-all-values'
                    )}
              </Checkbox>
              <Divider className={`${PF}__divider`} />
            </>
          )}

          <VirtualizedOptionsList
            key={searchValue}
            options={orderedOptions}
            checkedValuesSet={checkedValuesSet}
            disabled={disabled}
            onOptionChange={optionChangeHandler}
          />
        </FilterMenuShell>
      }
      trigger="click"
      open={open && !disabled}
      onOpenChange={setOpen}
      placement="bottomLeft"
      showArrow={false}
      arrow={false}
      rootClassName={cn(
        'FilterMenuShellPopover',
        `${PF}__popover-root`,
        popoverRootClassName
      )}
      classNames={{ body: `${PF}__popover-body` }}
      getPopupContainer={getPopupContainer}
    >
      <Button
        className={cn(`${PF}__button`, { [`${PF}__button--active`]: isActive })}
        size="small"
        htmlType="button"
        disabled={disabled}
        aria-expanded={open && !disabled}
        data-testid={dataTestId}
      >
        <span className={`${PF}__label`}>{title}</span>
        {count && <span className={`${PF}__count`}>{count}</span>}
        <DownOutlined
          className={cn(`${PF}__icon`, { [`${PF}__icon--opened`]: open })}
        />
      </Button>
    </Popover>
  );
};
